<template>
  <div
    :class="{
      'tw:self-end tw:ml-20': message.type === 'user',
      'tw:self-start tw:mr-20': message.type === 'agent',
    }"
    class="tw:w-fit tw:max-w-[80%]"
  >
    <div
      :class="{
        'tw:bg-black': message.type === 'user',
        'tw:bg-default-100': message.type === 'agent' && !message.isError,
        'tw:bg-red-50 tw:border tw:border-red-200': message.isError,
      }"
      class="tw:p-4 tw:relative tw:rounded-2xl"
    >
      <!-- Loading State -->
      <div v-if="message.isLoading" class="tw:flex tw:items-center tw:gap-3">
        <div class="tw:flex tw:gap-1">
          <div class="typing-dot"></div>
          <div class="typing-dot" style="animation-delay: 0.2s"></div>
          <div class="typing-dot" style="animation-delay: 0.4s"></div>
        </div>
        <p class="tw:text-default-700">
          {{ message.content }}
        </p>
      </div>

      <!-- Regular Message -->
      <div v-else>
        <!-- User attachment image (if present) -->
        <div v-if="message.type === 'user' && message.imageUrl" class="tw:mb-3">
          <img
            :alt="trans.download.alt_attached"
            :src="message.imageUrl"
            class="tw:max-h-32 tw:rounded-lg tw:border tw:border-white/20"
          />
        </div>

        <!-- User products (if present) -->
        <div v-if="message.type === 'user' && message.products && message.products.length > 0" class="tw:mb-3">
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <div
              v-for="(product, idx) in message.products"
              :key="idx"
              class="tw:flex tw:items-center tw:gap-1 tw:bg-white/10 tw:rounded-full tw:px-2 tw:py-1"
            >
              <img
                v-if="product.img"
                :alt="product.title"
                :src="product.img"
                class="tw:w-5 tw:h-5 tw:rounded-full tw:object-cover"
              />
              <span class="tw:text-xs tw:text-white/80 tw:max-w-20 tw:truncate">{{ product.title }}</span>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="message.isError" class="tw:flex tw:items-start tw:gap-2">
          <FontAwesomeIcon class="tw:text-red-500 tw:mt-0.5" icon="fa-solid fa-circle-exclamation" />
          <p class="tw:text-red-700">
            {{ message.content }}
          </p>
        </div>

        <!-- Normal Message -->
        <p
          v-else
          :class="{
            'tw:text-white': message.type === 'user',
            'tw:text-black': message.type === 'agent',
          }"
        >
          {{ message.content }}
        </p>

        <!-- Generated Image (agent only) -->
        <div v-if="message.type === 'agent' && message.imageUrl" class="tw:mt-4">
          <img
            :alt="trans.download.alt_generated"
            :src="message.imageUrl"
            class="tw:w-full tw:h-auto tw:rounded-lg tw:border tw:border-default-200"
          />

          <div class="tw:flex tw:justify-between tw:items-center tw:mt-2">
            <div class="tw:flex tw:items-center tw:gap-1">
              <div
                v-if="!message.isLoading && !feedbackSentiment"
                class="tw:flex tw:items-center tw:gap-1"
              >
                <EdroneButtonIcon size="medium" type="border" @click="handlePositiveFeedback">
                  <FontAwesomeIcon icon="fa-light fa-thumbs-up" />
                </EdroneButtonIcon>
                <EdroneButtonIcon size="medium" type="border" @click="handleNegativeFeedback">
                  <FontAwesomeIcon icon="fa-light fa-thumbs-down" />
                </EdroneButtonIcon>
              </div>
            </div>

            <EdroneButtonIcon size="medium" type="border" @click="handleDownload">
              <FontAwesomeIcon icon="fa-light fa-download" />
            </EdroneButtonIcon>
          </div>

          <!-- Feedback Modal -->
          <EdroneModal :index="999999991" :open="showFeedbackModal" @close="showFeedbackModal = false">
            <template #title>{{ trans.feedback.modal_title }}</template>
            <div class="tw:flex tw:flex-col tw:gap-4">
              <EdroneTextarea
                :placeholder="trans.feedback.modal_placeholder"
                :rows="4"
                :value="feedbackText"
                @input="feedbackText = $event"
              />
              <div class="tw:flex tw:justify-end tw:gap-2">
                <EdroneButton type="outline" @click="showFeedbackModal = false">
                  {{ trans.feedback.cancel }}
                </EdroneButton>
                <EdroneButton :loading="isSubmittingFeedback" type="solid" @click="submitNegativeFeedback">
                  {{ trans.feedback.submit }}
                </EdroneButton>
              </div>
            </div>
          </EdroneModal>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import EdroneModal from "edrone-components/EdroneModal.vue";
import EdroneTextarea from "edrone-components/EdroneTextarea.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { submitFeedback } from "vue-app/api/AiBannerService";

type ChatMessage = {
  type: "user" | "agent";
  content: string;
  imageUrl?: string;
  isLoading?: boolean;
  isError?: boolean;
  prompt?: string;
  modelId?: number;
  attachmentUrl?: string | null;
  products?: Array<{ title: string; img?: string }>;
};

interface Props {
  message: ChatMessage;
}

interface Emits {
  (e: "download", imageUrl: string): void;
}

const props = defineProps<Props>();
const _emit = defineEmits<Emits>();

const trans = window._trans._branding.aiBanner;

const feedbackSentiment = ref<"positive" | "negative" | null>(null);
const showFeedbackModal = ref(false);
const feedbackText = ref("");
const isSubmittingFeedback = ref(false);

const handleDownload = () => {
  if (props.message.imageUrl) {
    const link = document.createElement("a");
    link.href = "/ai-banner/download?url=" + props.message.imageUrl;
    link.download = `banner-${Date.now()}.png`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const handlePositiveFeedback = async () => {
  if (feedbackSentiment.value) return;

  feedbackSentiment.value = "positive";

  try {
    await submitFeedback({
      feedback: "👍",
      sentiment: "positive",
      prompt: props.message.prompt || "",
      modelId: props.message.modelId || 0,
      attachmentUrl: props.message.attachmentUrl || null,
      generatedImageUrl: props.message.imageUrl || null,
    });
    window.parent.showToast(trans.feedback.thanks, "success");
  } catch {
    feedbackSentiment.value = null;
  }
};

const handleNegativeFeedback = () => {
  showFeedbackModal.value = true;
};

const submitNegativeFeedback = async () => {
  isSubmittingFeedback.value = true;

  try {
    await submitFeedback({
      feedback: feedbackText.value || "👎",
      sentiment: "negative",
      prompt: props.message.prompt || "",
      modelId: props.message.modelId || 0,
      attachmentUrl: props.message.attachmentUrl || null,
      generatedImageUrl: props.message.imageUrl || null,
    });
    window.parent.showToast(trans.feedback.thanks, "success");
    showFeedbackModal.value = false;
    feedbackText.value = "";
    feedbackSentiment.value = "negative";
  } finally {
    isSubmittingFeedback.value = false;
  }
};
</script>

<style scoped>
.typing-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #6b7280;
  animation: typing 1.4s infinite ease-in-out;
}

@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
